import { useEffect, useRef, useState, type ReactNode } from 'react';

/**
 * Horizontal/vertical split panel с перетаскиваемыми разделителями.
 * Ширины (или высоты) сохраняются в localStorage по `id`, индивидуально per-user.
 *
 * Использование:
 *   <ResizableSplit id="inbox-cols" direction="row" sizes={[260, 0, 300]} mins={[200, 320, 240]}>
 *     <Pane1 />
 *     <Pane2 />
 *     <Pane3 />
 *   </ResizableSplit>
 *
 * Значение `0` означает "растягивается" (flex: 1).
 */
interface Props {
  id: string;
  direction: 'row' | 'col';
  /** initial sizes, 0 = stretch */
  sizes: number[];
  mins?: number[];
  children: ReactNode[];
}

function loadSizes(id: string, fallback: number[]): number[] {
  try {
    const raw = localStorage.getItem(`inbx.split.${id}`);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.length === fallback.length) return parsed;
  } catch {
    // localStorage недоступен (Safari private, quota) — используем fallback
  }
  return fallback;
}

function saveSizes(id: string, sizes: number[]): void {
  try {
    localStorage.setItem(`inbx.split.${id}`, JSON.stringify(sizes));
  } catch {
    // localStorage недоступен — не сохраняем, состояние сбросится при reload
  }
}

export function ResizableSplit({
  id,
  direction,
  sizes: initial,
  mins,
  children,
}: Props): JSX.Element {
  const [sizes, setSizes] = useState(() => loadSizes(id, initial));
  const containerRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ idx: number; startPos: number; startA: number; startB: number } | null>(
    null,
  );

  useEffect(() => {
    function move(e: MouseEvent): void {
      const drag = dragRef.current;
      if (!drag) return;
      const delta = (direction === 'row' ? e.clientX : e.clientY) - drag.startPos;
      const minA = mins?.[drag.idx] ?? 120;
      const minB = mins?.[drag.idx + 1] ?? 120;
      const isAStretch = initial[drag.idx] === 0;
      const isBStretch = initial[drag.idx + 1] === 0;
      const next = [...sizes];
      if (!isAStretch && !isBStretch) {
        // both fixed — adjust both
        const newA = Math.max(minA, drag.startA + delta);
        const newB = Math.max(minB, drag.startB - delta);
        next[drag.idx] = newA;
        next[drag.idx + 1] = newB;
      } else if (isAStretch) {
        // A stretches, B fixed → resizing changes B inversely
        const newB = Math.max(minB, drag.startB - delta);
        next[drag.idx + 1] = newB;
      } else {
        // A fixed, B stretches
        const newA = Math.max(minA, drag.startA + delta);
        next[drag.idx] = newA;
      }
      setSizes(next);
    }
    function up(): void {
      if (dragRef.current) {
        dragRef.current = null;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        saveSizes(id, sizes);
      }
    }
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    return () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
    };
  }, [sizes, initial, mins, direction, id]);

  function startDrag(idx: number, e: React.MouseEvent): void {
    const isRow = direction === 'row';
    dragRef.current = {
      idx,
      startPos: isRow ? e.clientX : e.clientY,
      startA: sizes[idx]!,
      startB: sizes[idx + 1]!,
    };
    document.body.style.cursor = isRow ? 'col-resize' : 'row-resize';
    document.body.style.userSelect = 'none';
  }

  return (
    <div ref={containerRef} className={`inbx-split inbx-split--${direction}`}>
      {children.map((child, i) => (
        <Fragment key={i}>
          <div
            className="inbx-split__pane"
            style={
              direction === 'row'
                ? sizes[i] === 0
                  ? { flex: 1 }
                  : { width: sizes[i], flex: '0 0 auto' }
                : sizes[i] === 0
                  ? { flex: 1 }
                  : { height: sizes[i], flex: '0 0 auto' }
            }
          >
            {child}
          </div>
          {i < children.length - 1 && (
            <div className="inbx-split__handle" onMouseDown={(e) => startDrag(i, e)} />
          )}
        </Fragment>
      ))}
    </div>
  );
}

// Local Fragment alias to avoid extra import noise
function Fragment({ children }: { children: ReactNode }): JSX.Element {
  return <>{children}</>;
}
